import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Box,
  Typography,
  Avatar,
} from "@mui/material";
// import { Person as PersonIcon } from "@mui/icons-material";
import api from "../../services/api";

/**
 * ColaboradoresMultiselect - Componente para seleccionar múltiples colaboradores
 * Soporta búsqueda en vivo y muestra avatar + nombre
 */
const ColaboradoresMultiselect = ({
  value = [],
  onChange,
  error,
  helperText,
  disabled = false,
  maxSelections = 10,
}) => {
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedColaboradores, setSelectedColaboradores] = useState([]);

  // Cargar colaboradores al montar
  useEffect(() => {
    loadColaboradores();
  }, []);

  // Actualizar selected cuando cambia value (prop)
  useEffect(() => {
    if (Array.isArray(value) && colaboradores.length > 0) {
      const selected = colaboradores.filter((c) => value.includes(c.id));
      setSelectedColaboradores(selected);
    }
  }, [value, colaboradores]);

  const loadColaboradores = async () => {
    try {
      setLoading(true);
      const response = await api.get("/colaboradores");
      const list = Array.isArray(response.data) ? response.data : [];
      // Filtrar solo colaboradores activos
      const activos = list.filter((col) => col.estado === "Activo");
      setColaboradores(activos);
    } catch (error) {
      setColaboradores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (searchValue) => {
    setInputValue(searchValue);
    if (searchValue.length < 2) {
      loadColaboradores();
      return;
    }

    try {
      setLoading(true);
      const response = await api.get("/colaboradores", {
        params: { search: searchValue },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      const activos = list.filter((col) => col.estado === "Activo");
      setColaboradores(activos);
    } catch (error) {
      // Error silencioso
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue.length > maxSelections) {
      return; // No permitir más de maxSelections
    }
    setSelectedColaboradores(newValue);
    onChange(newValue.map((col) => col.id));
  };

  const getAvatarColor = (nombre) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#FFA07A",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E2",
    ];
    const index = nombre.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Autocomplete
      multiple
      fullWidth
      disabled={disabled || loading}
      options={colaboradores}
      value={selectedColaboradores}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, value, reason) => {
        if (reason === "input" || reason === "reset") {
          handleSearch(value);
        }
      }}
      loading={loading}
      getOptionLabel={(option) => option?.nombre_completo || ""}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Colaboradores"
          placeholder="Buscar colaboradores..."
          error={!!error}
          helperText={
            helperText ||
            `${selectedColaboradores.length}/${maxSelections} seleccionados`
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            key={`${option.id}-${index}`}
            {...getTagProps({ index })}
            avatar={
              <Avatar
                sx={{
                  bgcolor: getAvatarColor(option.nombre_completo),
                  width: 24,
                  height: 24,
                  fontSize: "0.75rem",
                }}
              >
                {option.nombre_completo?.charAt(0).toUpperCase()}
              </Avatar>
            }
            label={option.nombre_completo}
            onDelete={getTagProps({ index }).onDelete}
            variant="outlined"
          />
        ))
      }
      renderOption={(props, option) => (
        <Box
          {...props}
          component="li"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Avatar
            sx={{
              bgcolor: getAvatarColor(option.nombre_completo),
              width: 32,
              height: 32,
              mr: 1.5,
            }}
          >
            {option.nombre_completo?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {option.nombre_completo}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {option.rol} • {option.ubicacion}
            </Typography>
          </Box>
        </Box>
      )}
      noOptionsText="No se encontraron colaboradores"
      slotProps={{
        paper: {
          sx: {
            "& .MuiAutocomplete-option": {
              padding: "8px !important",
            },
          },
        },
      }}
    />
  );
};

export default ColaboradoresMultiselect;
